/**
 * CommunityCommitmentsWidget — Dashboard widget that surfaces every community
 * goal the current user has joined. Pure deep-link list, no progress/todos.
 * Visible only when the user has ≥1 row in common_goal_participants.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryErrorState } from "@/components/common/QueryState";
import { Users, ArrowRight, BookMarked } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const MAX_VISIBLE = 3;

interface Row {
  id: string;
  title: string;
  sponsorName: string | null;
  sponsorLogoUrl: string | null;
  endDate: string;
  goalType: string | null;
}

export function CommunityCommitmentsWidget() {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["community-commitments", user?.id],
    queryFn: async (): Promise<Row[]> => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("common_goal_participants")
        .select("common_goal_id, joined_at, common_goals!inner(id, title, sponsor_name, sponsor_logo_url, end_date, goal_type)")
        .eq("user_id", user.id)
        .order("joined_at", { ascending: false });
      if (error) throw error;
      return (data || []).map((r: any) => ({
        id: r.common_goals.id,
        title: r.common_goals.title,
        sponsorName: r.common_goals.sponsor_name,
        sponsorLogoUrl: r.common_goals.sponsor_logo_url,
        endDate: r.common_goals.end_date,
        goalType: r.common_goals.goal_type ?? null,
      }));
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  if (isLoading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (error) {
    return (
      <QueryErrorState
        error={error}
        onRetry={() => refetch()}
        title="We couldn't load your community commitments"
      />
    );
  }
  if (!data || data.length === 0) return null;

  const visible = data.slice(0, MAX_VISIBLE);
  const more = data.length - visible.length;

  return (
    <Card className="p-4 border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/20">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          <h2 className="text-sm font-semibold">Community commitments</h2>
        </div>
        <Link
          to="/common-goals"
          className="text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
        >
          See all
        </Link>
      </div>

      <ul className="space-y-2">
        {visible.map((g) => (
          <li
            key={g.id}
            className="flex items-center gap-3 rounded-md bg-background/70 p-2 border border-border/50"
          >
            {g.sponsorLogoUrl ? (
              <img
                src={g.sponsorLogoUrl}
                alt={g.sponsorName || ""}
                className="h-8 w-8 rounded object-contain bg-white border"
                loading="lazy"
              />
            ) : (
              <div className="h-8 w-8 rounded bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center text-emerald-700 dark:text-emerald-300">
                <Users className="h-4 w-4" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium line-clamp-1">{g.title}</p>
              {g.sponsorName && (
                <p className="text-xs text-muted-foreground line-clamp-1">
                  with {g.sponsorName}
                </p>
              )}
            </div>
            {g.goalType === "discovery" ? (
              <Button
                asChild
                size="sm"
                className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Link to={`/community-goals/${g.id}`} aria-label={`View passport for ${g.title}`}>
                  <BookMarked className="h-3.5 w-3.5 mr-1" /> Passport
                </Link>
              </Button>
            ) : (
              <Button
                asChild
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-emerald-700 dark:text-emerald-300"
              >
                <Link to={`/common-goals?goal=${g.id}`} aria-label={`Open ${g.title}`}>
                  Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Link>
              </Button>
            )}
          </li>
        ))}
      </ul>

      {more > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          +{more} more on the Community Goals page
        </p>
      )}
    </Card>
  );
}
